import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { TableData } from "../../types/table-filter-types";
import {getReportHistory, getReports} from "../api";
import { formatTableTime } from "../../utils/format-table-dates";

export interface ResponseReports {
  id: number;
  cityName: string;
  governorate: string;
  status: string;
  userId: number;
  userName: string;
  creationDate: string;
  modificationDate: string;
}

export interface ReportRow {
  id: number;
  name: string;
  cityName: string;
  userName: string;
  status: string;
  governorate: string;
  modificationDateAndCreationDate: string;
}

export interface ReportHistoryLog {
  changedDateTime: string;
  id: number;
  status: string;
  userId: number;
  userName: string;
}

export interface ReportHistoryResponse {
  id: number;
  cityName: string;
  governorate: string;
  status: string;
  historyLogs: ReportHistoryLog[];
}


interface InitialState {
  fullData: TableData;
  data: TableData;
  totalDataSize: number;
  selected: number[];
  isLoading: boolean;
  revisionHistory: ReportHistoryResponse | null;
  historyLoading: boolean;
}

const initialState: InitialState = {
  fullData: [],
  data: [],
  totalDataSize: 0,
  selected: [],
  isLoading: false,
  revisionHistory: null,
  historyLoading: false,
};

export const fetchRevisionHistory = createAsyncThunk<ReportHistoryResponse, number>(
  "reportsJournal/fetchRevisionHistory",
  async (reportId) => {
    return await getReportHistory(reportId);
  },
);

export const fetchReports = createAsyncThunk(
  "reportsJournal/fetchReports",
  async () => {
    return await getReports();
  },
);

export const reportsJournalSlice = createSlice({
  name: "reportsJournal",
  initialState,
  reducers: {
    setData: (state, { payload }) => {
      state.data = payload.data;
      state.totalDataSize = payload.totalDataSize;
    },
    addSelected: (state, { payload }) => {
      state.selected.push(...payload);
    },
    deleteSelected: (state, { payload }) => {
      state.selected = state.selected.filter((id) => !payload.includes(id));
    },
    deleteAllSelected: (state) => {
      state.selected = [];
    },
    clearRevisionHistory: (state) => {
      state.revisionHistory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReports.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.isLoading = false;

        const template = "MM/DD/YYYY hh:mm AM";

        state.fullData = action.payload.map((item: ResponseReports) => {
          const hasValidModDate =
            item.modificationDate !== null;

          const modification = hasValidModDate
            ? formatTableTime(new Date(item.modificationDate), template)
            : " ";

          const creation = formatTableTime(
            new Date(item.creationDate),
            template,
          );

          return {
            ...item,
            modificationDateAndCreationDate: `${modification}\n${creation}`
          };
        }).sort((a, b) => b.id - a.id);
      })
      .addCase(fetchReports.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchRevisionHistory.pending, (state) => {
        state.historyLoading = true;
        state.revisionHistory = null;
      })
      .addCase(fetchRevisionHistory.fulfilled, (state, { payload }) => {
        state.historyLoading = false;

        const template = "MM/DD/YYYY hh:mm AM";

        const formattedHistoryLogs = payload.historyLogs.map(log => ({
          ...log,
          changedDateTime: formatTableTime(new Date(log.changedDateTime), template),
        }));

        state.revisionHistory = {
          ...payload,
          historyLogs: formattedHistoryLogs
        };
      })
      .addCase(fetchRevisionHistory.rejected, (state) => {
        state.historyLoading = false;
      });
  },
});

export const {
  setData,
  addSelected,
  deleteSelected,
  deleteAllSelected,
  clearRevisionHistory,
} = reportsJournalSlice.actions;

export default reportsJournalSlice.reducer;
